import { del, get, post } from 'utils/api';
import { IS_DEVELOPMENT } from 'constants/environment';

/**
 * Fetch AffordaPortalForms details based on provided parameters.
 *
 * @param {Object} params - Object containing organizationId, propertyId, and optional query filters.
 * @param {string} params.organizationId - The unique ID of the organization.
 * @param {string} params.propertyId - The unique ID of the property.
 * @param {string} [params.portalUserId] - Optional portal user ID for filtering.
 * @param {string} [params.householdId] - Optional household ID for filtering.
 * @param {string} [params.applicationId] - Optional application ID for filtering.
 * @param {string} [params.applicantId] - Optional applicant ID for filtering.
 * @param {Object} [params.options] - Optional request configuration.
 * @returns {Promise<any>} - The fetched data or an error object.
 */
export const getAffordaPortalForms = async ({
  organizationId,
  propertyId,
  portalUserId,
  householdId,
  applicationId,
  applicantId,
  options,
} = {}) => {
  const paramValues = {
    applicationId,
    applicantId,
    householdId,
    portalUserId,
  };

  // Remove undefined or empty values
  Object.keys(paramValues).forEach(
    (key) => !Boolean(paramValues[key]?.length) && delete paramValues[key],
  );

  const params = new URLSearchParams(paramValues);

  const url = `/${organizationId}/${propertyId}/afforda-portal-forms?${params}`;

  const response = await get(url, options);

  if (response.error) {
    return response.error;
  }

  return response;
};

/**
 * Fetch a single AffordaPortalForm by portalUserId.
 *
 * @param {Object} params - Object containing organizationId, propertyId, and portalUserId.
 * @param {string} params.organizationId - The unique ID of the organization.
 * @param {string} params.propertyId - The unique ID of the property.
 * @param {string} params.portalUserId - The portal user ID to fetch the record for.
 * @param {Object} [params.options] - Optional request configuration.
 * @returns {Promise<any>} - The fetched data or an error object.
 */
export const getAffordaPortalFormByPortalUserId = async ({
  organizationId,
  propertyId,
  portalUserId,
  options,
}) => {
  if (!portalUserId) {
    throw new Error('portalUserId is required.');
  }

  const url = `/${organizationId}/${propertyId}/afforda-portal-forms/${portalUserId}`;

  const response = await get(url, options);

  if (response.error) {
    return response.error;
  }

  return response;
};

/**
 * Create or update a record for AffordaPortalForms.
 *
 * @param {Object} params - Object containing necessary identifiers and payload.
 * @param {string} params.organizationId - The unique ID of the organization.
 * @param {string} params.propertyId - The unique ID of the property.
 * @param {Object} params.payload - The data to create or update the record with.
 * @param {Object} [params.options] - Optional request configuration.
 * @returns {Promise<any>} - The response from the server or an error object.
 */
export const createOrUpdateAffordaPortalForms = async ({
  organizationId,
  propertyId,
  portalUserId,
  payload,
  options,
}) => {
  const url = `/${organizationId}/${propertyId}/afforda-portal-forms/${portalUserId}`;
  const response = await post(url, payload, options);

  if (response.error) {
    if (IS_DEVELOPMENT) console.error(response.error);
    throw response.error;
  }

  return response;
};

/**
 * Delete a record for AffordaPortalForms by `id` or `portalUserId`.
 *
 * @param {Object} params - Object containing necessary identifiers and filters.
 * @param {string} params.organizationId - The unique ID of the organization.
 * @param {string} params.propertyId - The unique ID of the property.
 * @param {string} [params.id] - The unique ID of the record to delete.
 * @param {string} [params.portalUserId] - The portal user ID of the record to delete.
 * @param {Object} [params.options] - Optional request configuration.
 * @returns {Promise<any>} - The response from the server or an error object.
 */
export const deleteAffordaPortalForm = async ({
  organizationId,
  propertyId,
  portalUserId,
  options,
}) => {
  // Ensure `portalUserId` is provided
  if (!portalUserId) {
    throw new Error('`portalUserId` must be provided to delete a record.');
  }

  const url = `/${organizationId}/${propertyId}/afforda-portal-forms/${portalUserId}`;

  const response = await del(url, options);

  if (response.error) {
    return response.error;
  }

  return response;
};
